import { useEffect, useRef, useState } from "react";
import { getGenders } from "../../../../services/AppService";
import { resolveMediaUrl } from "../../../../utility/media";
import { getActiveDepartments, getActiveDesignations } from "../../../../services/AdminServices";
import { BACKEND_URL } from "../../../../services/Api";
import { IoClose } from "react-icons/io5";
import { useTranslation } from "react-i18next";

const AddStaffEmployeeForm = ({ onSubmit, editData, onClose, open }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: "",
        employeeNumber: "",
        contactPrimary: "",
        contactSecondary: "",
        panNo: "",
        aadhaarNo: "",
        email: "",
        departmentId: "",
        designationId: "",
        active: true
    });

    const [preview, setPreview] = useState(null);
    const [fileName, setFileName] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [image, setImage] = useState(null);
    const [error, setError] = useState(null);

    const fileInputRef = useRef(null);


    // 1. Initial Load: Fetch Masters
    useEffect(() => {
        getActiveDepartments().then(setDepartments).catch(console.error);
        getActiveDesignations().then(setDesignations).catch(console.error);
    }, []);

    // 2. Edit Mode: Populate Data
    useEffect(() => {
        if (editData) {
            setFormData({
                name: editData.name || "",
                employeeNumber: editData.employeeNumber || "",
                contactPrimary: editData.contactPrimary || "",
                contactSecondary: editData.contactSecondary || "",
                panNo: editData.panNo || "",
                aadhaarNo: editData.aadhaarNo || "",
                email: editData.email || "",
                departmentId: editData.departmentId || "",
                designationId: editData.designationId || "",
                active: editData.active ?? true
            });
            if (editData.photoUrl) {
                setPreview(resolveMediaUrl(editData.photoUrl));
            }
        }
    }, [editData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Convert ID strings to Numbers for backend compatibility
        const finalValue = (name === "departmentId" || name === "designationId") ? Number(value) :
            (name === "active" ? value === "true" : value);

        setFormData(prev => ({ ...prev, [name]: finalValue }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImage(file);
            setPreview(URL.createObjectURL(file));
            setFileName(file.name);
        }
    };

    const handleRemoveImage = () => {
        setImage(null);
        setPreview(null);
        setFileName(null);
        // const fileInput = document.querySelector('input[name="photo"]');
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Build the JSON DTO
        const employeeDto = {
            ...formData,
            id: editData?.id || null,
            photo: preview === null ? "REMOVE" : (editData?.photo || null)
        };

        const finalFormData = new FormData();
        // Match the @RequestPart("employee") in Java
        finalFormData.append(
            "employee",
            new Blob([JSON.stringify(employeeDto)], { type: "application/json" })
        );

        // Match the @RequestPart("photo") in Java
        // const photoFile = e.target.photo.files[0];
        // if (photoFile) {
        //     finalFormData.append("photo", photoFile);
        // }

        if (image) {
            finalFormData.append("photo", image);
        }

        onSubmit(finalFormData);
    };

    const formFields = [
        { name: "name", label: t('forms.employee.fullName'), type: "text", placeholder: t('forms.employee.fullNamePlc') },
        { name: "employeeNumber", label: t('forms.employee.employeeNumber'), type: "text", placeholder: t('forms.employee.employeeNumberPlc') },
        { name: "contactPrimary", label: t('forms.employee.contactPrimary'), type: "text", placeholder: t('forms.employee.contactPrimaryPlc'), maxLength: 10 },
        { name: "contactSecondary", label: t('forms.employee.contactSecondary'), placeholder: t('forms.employee.contactSecondaryPlc'), maxLength: 10, required: false },
        { name: "email", label: t('forms.employee.email'), type: "email", placeholder: t('forms.employee.emailPlc') },
        { name: "panNo", label: t('forms.employee.panNo'), type: "text", placeholder: t('forms.employee.panNoPlc') },
        { name: "aadhaarNo", label: t('forms.employee.aadhaarNo'), type: "text", placeholder: t('forms.employee.aadhaarNoPlc') },
    ];

    if (!open) return null;

    return (

        <div className="staff-modal-overlay">
            <div className="staff-modal">
                <div className="staff-modal-header">
                    <div className="staff-modal-title">
                        {editData ? t('pageTitles.editEmployee') : t('pageTitles.addEmployee')}
                    </div>
                    <button className="admin-modal-close" onClick={onClose} type="button">
                        <IoClose />
                    </button>
                </div>

                <div className="staff-form-body">
                    {error && <div className="staff-error-box">{error}</div>}

                    <form className="staff-form-grid" onSubmit={handleSubmit}>
                        {/* DYNAMIC TEXT FIELDS */}
                        {formFields.map((field) => (
                            <div key={field.name} className="form-group">
                                <label className="admin-label">
                                    {field.label} {field.name !== "contactSecondary" && <span>*</span>}
                                </label>
                                <input
                                    className="admin-input"
                                    name={field.name}
                                    type={field.type || "text"}
                                    value={formData[field.name]}
                                    onChange={handleChange}
                                    placeholder={field.placeholder}
                                    required={field.name !== "contactSecondary"}
                                    maxLength={field.maxLength}
                                />
                            </div>
                        ))}

                        {/* DEPARTMENT */}
                        <div className="form-group">
                            <label className="staff-modal-label">{t('forms.employee.department')} <span>*</span></label>
                            <select name="departmentId" required value={formData.departmentId} onChange={handleChange} className="staff-modal-input">
                                <option value="">Select Department</option>
                                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                        </div>

                        {/* DESIGNATION */}
                        <div className="form-group">
                            <label className="staff-modal-label">{t('forms.employee.designation')} <span>*</span></label>
                            <select name="designationId" required value={formData.designationId} onChange={handleChange} className="staff-modal-input">
                                <option value="">Select Designation</option>
                                {designations.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                        </div>

                        {/* PHOTO UPLOAD SECTION */}
                        <div className="col-span-full border-t pt-4 mt-2">
                            <label className="staff-modal-label">{t('forms.employee.profilePhoto')}</label>
                            <div className="md:flex items-center gap-4 mt-2">
                                <input
                                    ref={fileInputRef}
                                    name="photo"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    className="staff-modal-input"
                                    style={{ width: 'auto' }}
                                />
                                {preview && (
                                    <div style={{ position: "relative" }}>
                                        <img src={preview} alt="Preview" style={{ width: "70px", height: "70px", borderRadius: "8px", objectFit: "cover", border: "1px solid #ddd" }} />
                                        <button type="button" onClick={handleRemoveImage} style={{ position: "absolute", top: "-5px", right: "-5px", background: "red", color: "white", borderRadius: "50%", width: "20px", height: "20px", border: "none", cursor: "pointer" }}>×</button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* SUBMIT BUTTON */}
                        <div className="col-span-full flex justify-end gap-3 mt-4">
                            <button type="button" className="staff-btn-cancel" onClick={onClose}>{t('common.close')}</button>
                            <button type="submit" className="staff-btn-save">
                                {editData ? t('common.update') : t('common.save')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>



    );
};

export default AddStaffEmployeeForm;


{/* <form onSubmit={handleSubmit} className="grid grid-cols-1 border-2 py-4 px-6 md:grid-cols-2 gap-x-8 gap-y-4 bg-white">
            {formFields.map((field) => (
                 <div
                    key={field.name}
                    className="flex flex-col sm:flex-row sm:items-center gap-2"
                >
                    <label className="sm:w-40 text-sm font-medium">
                        {field.label}
                        <span className="text-red-500">*</span>
                    </label>
                    <input
                        name={field.name}
                        type={field.type}
                        required
                        placeholder={field.placeholder}
                        value={formData[field.name]}
                        onChange={handleChange}
                        maxLength={field.maxLength}
                        className="flex-1 border border-gray-400 rounded px-3 py-2"
                    />
                </div>
            ))}

            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <label className="sm:w-40 text-sm font-medium">Department<span className="text-red-500">*</span></label>
                <select name="departmentId" required value={formData.departmentId} onChange={handleChange} className="flex-1 border border-gray-400 rounded px-3 py-2">
                    <option value="">Select Department</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <label className="sm:w-40 text-sm font-medium">Designation*</label>
                <select name="designationId" required value={formData.designationId} onChange={handleChange} className="flex-1 border border-gray-400 rounded px-3 py-2">
                    <option value="">Select Designation</option>
                    {designations.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
            </div>

            <div className="col-span-full flex flex-col gap-2 border-t pt-4">
                <label className="text-sm font-medium">Profile Photo</label>
                <div className="flex items-center gap-4">
                    <input name="photo" type="file" accept="image/*" onChange={handleFileChange} className="text-sm" />
                    {preview && (
                        <div className="relative inline-block">
                            <img src={preview} alt="preview" className="w-20 h-20 rounded-full border-2 border-blue-500 object-cover" />
                            <button type="button" onClick={handleRemoveImage} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 text-xs">✕</button>
                        </div>
                    )}
                </div>
            </div>

            <div className="col-span-full flex justify-center mt-6">
                <button type="submit" className="px-10 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    {editData ? "UPDATE STAFF" : "SAVE STAFF"}
                </button>
            </div>
        </form> */}